import { ItemType, MetaDataType } from '@szn/suggestlist';

/**
 * @typedef {import("@szn/suggestlist").ITextItem} ITextItem
 * @typedef {import("@szn/suggestlist").ILabelItem} ILabelItem
 */

/**
 * @typedef {object} IRibbonExtraData
 * @property {boolean} [isTrending]
 * @property {boolean} [isTopQuery]
 * @property {boolean} [openUrl] - TODO: presun do konfigu nebo smazat
 * @property {string} [url]
 *
 * @typedef {ITextItem & IRibbonExtraData} IRibbonTextItem
 *
 * @typedef {(IRibbonTextItem|ILabelItem)} RibbonSuggestItem
 */

/**
 * Trendings API result definition
 * @typedef {Object} ITrendingItem
 * @property {string} [ctx]
 * @property {string} query
 * @property {string} url
 */

/**
 * Articles API result definition
 * @typedef {Object} IArticleSuggestion
 * @property {string} Title
 * @property {string} Url
 * @property {string} ctx
 */

/**
 * Authors API result definition
 * @typedef {Object} IAuthorUserData
 * @property {string} mediaUrl
 * @property {string} authorUrl
 * @property {string} name
 * @property {string} description
 *
 * @typedef {Object} IAuthorSuggestion
 * @property {string} sentence
 * @property {IAuthorUserData} [userData]
 */

const MAX_HISTORY_SUGGESTIONS = 4;
const MAX_ARTICLES = 5;
const ARTICLES_CHAR_LIMIT = 2;

export default class SuggestApi {
	/**
	 * @private
	 * @type {(string|undefined)}
	 */
	get _articlesApi() {
		return this._widget.props.search?.articlesApiUrl;
	}

	/**
	 * @private
	 * @type {number}
	 */
	get _articlesApiCharLimit() {
		return this._widget.props.search?.articlesApiCharLimit || ARTICLES_CHAR_LIMIT;
	}

	/**
	 * @private
	 * @type {boolean}
	 */
	get _genericSuggestionsOnTop() {
		return this._widget.props.search?.genericSuggestionsOnTop || false;
	}

	/**
	 * @private
	 * @type {(string|undefined)}
	 */
	get _serviceSuggestionsApi() {
		return this._widget.props.search?.suggestionsApiUrl;
	}

	/**
	 * @private
	 * @type {(string|undefined)}
	 */
	get _serviceSuggestionsLabel() {
		return this._widget.props.search?.suggestionsLabel;
	}

	constructor(widget) {
		if (!widget) {
			throw new Error('No widget provided');
		}

		/**
		 * NOTE: "search" prop MUST be always referenced thru widget object.
		 * Change of settings in playground dont notify SuggestApi nor create new instance.
		 * Widget reference is stable during whole app live.
		 * In order to keep accurate "search" settings. Getters were introduced.
		 *
		 * NOTE2: "environment" prop is destroyed during switch of settings.
		 * MUST be set in constructor!
		 */
		this._widget = widget;

		// For some reason, environment this must be set during setup.
		// switching in playground will erase it
		/**
		 * @type {string}
		 */
		this._suggestionsApi = widget.props.environment.suggestApiUrl;
		/**
		 * @type {string}
		 */
		this._trendingApi = widget.props.environment.trendingApiUrl;
		this._defaultConf = {
			cache: 'no-store',
			credentials: 'include'
		};

		// Only for dev
		// if (typeof window !== 'undefined') {
		// 	window.suggestApi = this;
		// }
	}

	async load(query = '', cursorPosition = 0, trendingTopics = false) {
		const enableServiceSuggestions =
			this._serviceSuggestionsApi && this._serviceSuggestionsLabel;
		const enableArticles = !!this._articlesApi;
		const showGenericSuggestionsLabel =
			this._widget.props.search?.showGenericSuggestionsLabel;

		/**
		 * @type {(Promise<RibbonSuggestItem[]>|undefined)}
		 */
		let serviceSuggestionsPromise;
		/**
		 * @type {(Promise<RibbonSuggestItem[]>|undefined)}
		 */
		let trendingTopicsPromise;

		if (enableServiceSuggestions) {
			serviceSuggestionsPromise = this.loadServiceSuggestions(query, cursorPosition).then(
				queries =>
					this.transformSuggestions(
						queries,
						MAX_HISTORY_SUGGESTIONS,
						this._serviceSuggestionsLabel,
						true
					)
			);
		}

		// Specialka pro Garaz - TODO: zbavit se co nejdrive, upravou api
		if (enableArticles && query.length >= this._articlesApiCharLimit) {
			serviceSuggestionsPromise = this.loadArticles(query, MAX_ARTICLES).then(queries =>
				this.transformArticles(queries)
			);
		}

		// Trending topics
		if (trendingTopics && !this._widget.props.search?.noTrendingTopics) {
			trendingTopicsPromise = this.loadTrending().then(queries =>
				this.transformTrending(queries)
			);
		}

		/**
		 * @type {Promise<RibbonSuggestItem[]>}
		 */
		const fulltextSuggestionsPromise = this.loadSuggestions(query, cursorPosition).then(
			queries =>
				this.transformSuggestions(
					queries,
					MAX_HISTORY_SUGGESTIONS,
					showGenericSuggestionsLabel ? 'Hledat na internetu' : undefined
				)
		);

		const [fulltextSuggestions, trendingItems = [], serviceSuggestions = []] =
			await Promise.all([
				fulltextSuggestionsPromise,
				trendingTopicsPromise,
				serviceSuggestionsPromise
			]);

		if (this._genericSuggestionsOnTop) {
			return [].concat(fulltextSuggestions, serviceSuggestions, trendingItems);
		}

		return [].concat(serviceSuggestions, fulltextSuggestions, trendingItems);
	}

	async removeSuggestionFromHistory(item) {
		const phrase = item.text.map(chunk => chunk.text).join('');

		return this.request({
			...this._defaultConf,
			url: this._suggestionsApi,
			query: {
				phrase
			},
			method: 'DELETE'
		});
	}

	async request(requestConfig, ...rest) {
		const { url, query, ...restConfig } = requestConfig;
		const parsedUrl = new URL(url);
		// query < url.searchParams
		// allows to override query params from serivce settings (highlight, format, etc.)
		const newRequestConfig = {
			query: {
				...requestConfig.query,
				...Object.fromEntries(parsedUrl.searchParams)
			},
			...restConfig
		};
		parsedUrl.search = '';
		newRequestConfig.url = parsedUrl.toString();

		return this._widget.http.request(newRequestConfig, ...rest);
	}

	/**
	 * Request article suggestions
	 * @param {string} phrase
	 * @param {number} [count]
	 * @returns {Promise<ITextItem[]>}
	 */
	async loadSuggestions(phrase, cursorPosition = 0, count = 6) {
		return this.request({
			...this._defaultConf,
			url: this._suggestionsApi,
			query: {
				phrase,
				cursorPosition,
				count,
				format: 'json-2',
				highlight: 1
			}
		})
			.then(({ response }) => response?.body?.result || [])
			.catch(error => {
				console.error('Suggestions API error:', error);

				return [];
			});
	}

	/**
	 * Request article suggestions
	 * @param {string} phrase
	 * @param {number} [count]
	 * @returns {Promise<ITextItem[]>}
	 */
	async loadServiceSuggestions(phrase, cursorPosition = 0) {
		return this.request({
			...this._defaultConf,
			url: this._serviceSuggestionsApi,
			query: {
				phrase,
				cursorPosition,
				format: 'json-2',
				highlight: 1
			}
		})
			.then(({ response }) => response?.body?.result || [])
			.catch(error => {
				console.error('Suggestions API error:', error);

				return [];
			});
	}

	/**
	 * Request article suggestions
	 * @param {string} phrase
	 * @param {number} [count]
	 * @returns {Promise<IArticleSuggestion[]>}
	 */
	async loadArticles(phrase, count = 5) {
		return this.request({
			cache: 'no-store',
			credentials: 'omit',
			url: this._articlesApi,
			query: {
				query: phrase,
				limit: count
			}
		})
			.then(({ response }) => response?.body?.Results || [])
			.catch(error => {
				console.error('Articles API error:', error);

				return [];
			});
	}

	/**
	 * Request trendings
	 * @returns {Promise<ITrendingItem[]>}
	 */
	async loadTrending() {
		return this.request({
			url: this._trendingApi,
			...this._defaultConf
		})
			.then(({ response }) => response?.body?.queries || [])
			.catch(error => {
				console.error('Trending API error:', error);

				return [];
			});
	}

	/**
	 * Transform article suggestions to suggest item format
	 * @param {IArticleSuggestion[]} queries
	 * @returns {RibbonSuggestItem[]}
	 */
	async transformArticles(queries = []) {
		if (queries.length === 0) {
			return [];
		}

		return queries.map(item => ({
			asideMetaData: null,
			ctx: item.ctx,
			isTrending: true,
			itemType: ItemType.TEXT,
			leadingMetaData: {
				priority: 0,
				type: MetaDataType.GRAPHICS,
				value: this._widget.props.service.icon.src,
				fit: '20px 20px'
			},
			openUrl: true,
			text: [{ text: item.Title, emphasized: true }],
			trailingMetaData: null,
			url: item.Url
		}));
	}

	/**
	 * Transform suggestions to correct format
	 * @param {Array<(IAuthorSuggestion|ITextItem)>} queries
	 * @param {number} [limit=MAX_HISTORY_SUGGESTIONS]
	 * @param {string} [label]
	 * @param {boolean} [isServiceSuggestions=false]
	 * @returns {RibbonSuggestItem[]}
	 */
	async transformSuggestions(
		queries = [],
		limit = MAX_HISTORY_SUGGESTIONS,
		label,
		isServiceSuggestions = false
	) {
		if (queries.length === 0) {
			return [];
		}

		const suggestions = [];

		if (label) {
			suggestions.push({
				itemType: ItemType.LABEL,
				text: label
			});
		}

		if (isServiceSuggestions) {
			// eslint-disable-next-line no-param-reassign
			queries = queries.map(query => {
				// Seznam Medium Specialka - TODO: upravit data v API
				if ('userData' in query && query.userData.authorUrl) {
					return {
						...query,
						openUrl: true,
						url: query.userData.authorUrl,
						leadingMetaData: {
							priority: 0,
							type: MetaDataType.GRAPHICS,
							value: query.userData.mediaUrl || this._widget.props.service.icon.src
						},
						trailingMetaData: query.userData.description
							? {
									type: MetaDataType.TEXT,
									value: query.userData.description
							  }
							: null
					};
				}

				return {
					...query,
					isTopQuery: true
				};
			});
		}

		return suggestions.concat(queries.slice(0, limit));
	}

	/**
	 * Transform trendings to suggest item format
	 * @param {ITrendingItem[]} queries
	 * @param {number} [limit=MAX_HISTORY_SUGGESTIONS]
	 * @returns {RibbonSuggestItem[]}
	 */
	async transformTrending(queries = [], limit = MAX_HISTORY_SUGGESTIONS) {
		if (queries.length === 0) {
			return [];
		}

		return [
			{
				itemType: ItemType.LABEL,
				text: 'Hledá se na Seznam.cz' // data.promo
			},
			...queries.slice(0, limit).map(item => ({
				asideMetaData: null,
				ctx: item.ctx,
				isTrending: true,
				itemType: ItemType.TEXT,
				leadingMetaData: {
					priority: 0,
					type: MetaDataType.GRAPHICS,
					value: 'https://d55-a.sdn.cz/d_55/c_img_E_L/H32XnX.png'
				},
				query: item.query,
				text: [{ text: item.query, emphasized: true }],
				trailingMetaData: null,
				url: item.url
			}))
		];
	}
}
